/**
 * Putting a merge tag where the writer was last typing — 18 Sep 2026.
 *
 * Split from `mergeFields.ts`, which has to stay free of the DOM so the check
 * script can import it under Node. This half is the part that cannot.
 *
 * ## It watches the document, not the editor
 *
 * Deliberately. The subject line lives in the workspace header, outside the
 * block editor, and the subject is where a merge tag earns the most — it is the
 * line somebody decides whether to open on. Scoping the listener to the editor
 * would have made the one field that matters most the one field the panel could
 * not reach.
 */
import { useCallback, useEffect, useRef } from 'react'

/**
 * Put a tag where the writer was last typing.
 *
 * Two routes in, because the editor has two kinds of field:
 *
 * * a **`contentEditable`** — the rich text box — where `execCommand` inserts at
 *   the caret and fires the `input` event its `onChange` is already listening
 *   for. Deprecated and still the only thing that reliably preserves an undo
 *   stack, which is the same reason `RichText` uses it for bold and italic.
 * * a **controlled `<input>`**, where assigning `.value` is invisible to React —
 *   it tracks the previous value on the DOM node and skips the change as a
 *   no-op. Going through the prototype's setter is what makes React notice, and
 *   it is the standard way round this rather than a trick of ours.
 *
 * Returns false when there is nowhere to put it, so the caller can fall back to
 * the clipboard instead of silently doing nothing.
 */
export function useMergeInsert(): { insert: (tag: string) => boolean } {
  const last = useRef<HTMLElement | null>(null)

  useEffect(() => {
    const onFocus = (e: FocusEvent) => {
      const t = e.target
      if (!(t instanceof HTMLElement)) return
      // A colour swatch and a number spinner are text fields as far as the DOM
      // is concerned, and a merge tag in either is nonsense.
      if (t instanceof HTMLInputElement && (t.type === 'text' || t.type === '')) last.current = t
      else if (t instanceof HTMLTextAreaElement) last.current = t
      else if (t.isContentEditable) last.current = t
    }
    document.addEventListener('focusin', onFocus)
    return () => document.removeEventListener('focusin', onFocus)
  }, [])

  const insert = useCallback((tag: string): boolean => {
    const el = last.current
    // A field that has been unmounted — the section was deleted while the panel
    // was open — is not somewhere to insert into.
    if (!el || !el.isConnected) return false
    const text = `{{${tag}}}`

    if (el.isContentEditable) {
      el.focus()
      document.execCommand('insertText', false, text)
      return true
    }

    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      el.focus()
      const start = el.selectionStart ?? el.value.length
      const end = el.selectionEnd ?? start
      const next = el.value.slice(0, start) + text + el.value.slice(end)
      const proto =
        el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
      const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set
      if (!setter) return false
      setter.call(el, next)
      el.dispatchEvent(new Event('input', { bubbles: true }))
      // After the value has been written, so React's re-render does not move it.
      el.setSelectionRange(start + text.length, start + text.length)
      return true
    }

    return false
  }, [])

  return { insert }
}
